import "../App.css";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Grid,
  Typography,
} from "@mui/material";
import PasswordField from "../PasswordField";
import { Alert, Spinner } from "react-bootstrap";
import * as Yup from "yup";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import countryToCurrency from "country-to-currency";
import axios from "axios";
import { API_BASE_URL, DISABLE_CAPTCHA, LOCAL_CAPTCHA_TOKEN } from "../../config/config";
import CaptchaField from "../CaptchaField";
import { Link, useNavigate } from "react-router-dom";

const EventsRegistration = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedEventInvite, setSelectedEventInvite] = useState(null);
  const [recaptchaToken, setRecaptchatToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  const handleEventInviteChange = (event) => {
    const file = event.target.files[0];
    setSelectedEventInvite(file);
  };

  const calculateCareValues = (values) => {
    const carePercentage = 0.01;
    values.careFees = (
      parseFloat(values.careValueAmount) * carePercentage
    ).toFixed(2);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();

      values.services.forEach((service, index) => {
        calculateCareValues(service);

        formData.append("eventInvite", selectedEventInvite);

        Object.entries(service).forEach(([key, value]) => {
          if (key !== "eventInvite") {
            formData.append(`services[${index}][${key}]`, value);
          }
        });
      });

      Object.keys(values).forEach((key) => {
        if (key !== "services") {
          formData.append(key, values[key]);
        }
      });

      formData.append(
        "recaptchaToken",
        DISABLE_CAPTCHA ? LOCAL_CAPTCHA_TOKEN : recaptchaToken
      );

      console.log(values);

      await axios.post(`${API_BASE_URL}/register`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Error:", error.response?.data?.error || error.message);
      setError("Error submitting the form");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (token) => {
    if (token) {
      setRecaptchatToken(token);
    }
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={["DatePicker"]}>
          <Formik
            initialValues={{
              services: [
                {
                  type: "Events",
                  category: "",
                  eventDetails: "",
                  eventDays: "",
                  expectedAttendees: "",
                  eventVenue: [],
                  emergencyContact: "",
                  eventInvite: "",
                  referralCode: "",
                  currency: "",
                  careFees: 0,
                  careValueAmount: 0,
                  careExpireTimeline: "1 Year",
                },
              ],
              password: "",
              email: "",
              fName: "",
              mName: "",
              lName: "",
              country: "",
              city: "",
              state: "",
              zip: "",
              contact: "",
            }}
            validationSchema={Yup.object({
              password: Yup.string()
                .required("Password is required")
                .matches(
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                  "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 digit, 1 special character, and be at least 8 characters long"
                ),
              email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
              fName: Yup.string().required("First Name is required"),
              lName: Yup.string().required("Last Name is required"),
              country: Yup.string().required("Country is required"),
              city: Yup.string(),
              state: Yup.string(),
              zip: Yup.string()
                .required("Zip is required")
                .matches(/^\d+$/, "Zip must only contain digits")
                .min(5, "Zip must be at least 5 digits")
                .max(10, "Zip cannot exceed 10 digits"),
              contact: Yup.string()
                .required("Contact is required")
                .matches(/^\d+$/, "Contact must only contain digits")
                .min(7, "Contact must be at least 7 digits")
                .max(15, "Contact cannot exceed 15 digits"),
            })}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form noValidate>
                {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
                  <Alert variant="warning" style={{ marginBottom: "1rem" }}>
                    Please correct the highlighted errors before submitting.
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="email" id="emailLabel">
                      Email
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="email"
                      id="email"
                    />
                    <ErrorMessage name="email" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="password" id="passwordLabel">
                      Set Your Password
                    </InputLabel>
                    <Field
                      name="password"
                      id="password"
                      component={PasswordField}
                      style={{ width: "100%" }}
                      size="small"
                    />
                    <ErrorMessage name="password" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="fName" id="fNameLabel">
                      First Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="fName"
                      id="fName"
                    />
                    <ErrorMessage name="fName" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="mName" id="mNameLabel">
                      Middle Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="mName"
                      id="mName"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="lName" id="lNameLabel">
                      Last Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="lName"
                      id="lName"
                    />
                    <ErrorMessage name="lName" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <InputLabel htmlFor="country" id="countryLabel">
                      Select Country
                    </InputLabel>
                    <CountryDropdown
                      value={formik.values.country}
                      onChange={(val) => formik.setFieldValue("country", val)}
                      className="form-control"
                      name="country"
                      id="country"
                    />
                    <ErrorMessage name="country" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="state" id="stateLabel">
                      State
                    </InputLabel>
                    <Field
                      as={RegionDropdown}
                      country={formik.values.country}
                      value={formik.values.state}
                      onChange={(val) => formik.setFieldValue("state", val)}
                      className="form-control"
                      name="state"
                      id="state"
                    />
                    <ErrorMessage name="state" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="city" id="cityLabel">
                      City
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="city"
                      id="city"
                    />
                    <ErrorMessage name="city" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="zip" id="zipLabel">
                      Zip Code
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="zip"
                      id="zip"
                    />
                    <ErrorMessage name="zip" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <InputLabel htmlFor="contact" id="contactLabel">
                      Contact
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="contact"
                      id="contact"
                    />
                    <ErrorMessage name="contact" />
                  </Grid>
                </Grid>
                {formik.values.services.map((service, index) => (
                  <div key={index}>
                    <Grid container spacing={2} style={{ marginTop: 0 }}>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].category`}>
                          Select Product Category
                        </InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].category`}
                          id={`services[${index}].category`}
                        >
                          <MenuItem value="Wedding">Wedding</MenuItem>
                          <MenuItem value="Corporate Events">
                            Corporate Events
                          </MenuItem>
                          <MenuItem value="Private Events">
                            Private Events
                          </MenuItem>
                        </Field>
                        <ErrorMessage name={`services[${index}].category`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].eventDetails`}>
                          Event Details
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].eventDetails`}
                          id={`services[${index}].eventDetails`}
                        />
                        <ErrorMessage
                          name={`services[${index}].eventDetails`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].eventDays`}>
                          Event Days
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].eventDays`}
                          id={`services[${index}].eventDays`}
                        />
                        <ErrorMessage name={`services[${index}].eventDays`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].expectedAttendees`}
                        >
                          Expected Number of Attendees
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].expectedAttendees`}
                          id={`services[${index}].expectedAttendees`}
                        />
                        <ErrorMessage
                          name={`services[${index}].expectedAttendees`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
  <InputLabel htmlFor={`services[${index}].eventVenue`}>
    Event Venue
  </InputLabel>
  {service.eventVenue.map((venue, venueIndex) => (
    <div key={venueIndex}>
      <Field
        as={TextField}
        style={{ width: "100%", marginBottom: "20px" }}
        size="small"
        name={`services[${index}].eventVenue[${venueIndex}]`}
        id={`services[${index}].eventVenue[${venueIndex}]`}
        value={venue}
      />
    </div>
  ))}
  <Button
    type="button"
    onClick={() =>
      formik.setFieldValue(
        `services[${index}].eventVenue`,
        [...service.eventVenue, ""]
      )
    }
    color="success"
    variant="contained"
  >
    Add More
  </Button>
</Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].emergencyContact`}
                        >
                          Emergency Contact Person Details
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].emergencyContact`}
                          id={`services[${index}].emergencyContact`}
                        />
                        <ErrorMessage
                          name={`services[${index}].emergencyContact`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].eventInvite`}>
                          Event Invite
                        </InputLabel>
                        <input
                          style={{ width: "100%" }}
                          size="small"
                          type="file"
                          name={`services[${index}].eventInvite`}
                          id={`services[${index}].eventInvite`}
                          onChange={handleEventInviteChange}
                        />
                        <ErrorMessage name={`services[${index}].eventInvite`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].currency`}>
                          Select Currency
                        </InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].currency`}
                          id={`services[${index}].currency`}
                        >
                          <MenuItem value="">Select Currency</MenuItem>
                          {currencies.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </Field>
                        <ErrorMessage name={`services[${index}].currency`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].careValueAmount`}
                        >
                          Enter Care Value
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].careValueAmount`}
                          id={`services[${index}].careValueAmount`}
                        />
                        <ErrorMessage
                          name={`services[${index}].careValueAmount`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel
                          htmlFor={`services[${index}].careFees`}
                          align="center"
                        >
                          Care Fees to Pay
                        </InputLabel>
                        <Typography variant="h6" align="center" name={`services[${index}].careFees`}>
                          {service.careValueAmount &&
                            `${(service.careValueAmount * 0.01).toFixed(2)} ${
                              service.currency
                            }`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel
                          htmlFor={`services[${index}].careValueAmount`}
                          align="center"
                        >
                          Your Care Value for 1st Year will be
                        </InputLabel>
                        <Typography
                          variant="body1"
                          align="center"
                          name={`services[${index}].careValueAmount`}
                          id={`services[${index}].careValueAmount`}
                        >
                          {service.careValueAmount} {service.currency}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                <CaptchaField
                  onChange={handleCaptchaChange}
                  wrapperStyle={{ display: "flex" }}
                  style={{
                    margin: "0 auto",
                    textAlign: "center",
                    marginTop: "2rem",
                  }}
                />
                <div className="submission-popup">
                  <div className="loading">
                    {loading && <Spinner animation="border" />}
                  </div>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && (
                    <Alert variant="success">
                      Form submitted successfully!
                    </Alert>
                  )}
                </div>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      disabled={formik.isSubmitting || loading}
                      style={{ marginTop: "1rem" }}
                    >
                      {formik.isSubmitting || loading
                        ? "Submitting..."
                        : "Register"}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DemoContainer>
      </LocalizationProvider>
      <div style={{ marginTop: "1rem", textAlign: "center" }}>
        <Typography variant="body1">
          Already a user? <Link to="/login">Login</Link>
        </Typography>
      </div>
    </div>
  );
};

export default EventsRegistration;
